import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import { apiRequest } from '../../lib/queryClient';

const RESOURCES = ['recipes', 'ingredients', 'materials', 'equipment', 'orders', 'vouchers', 'analytics'];
const ACTIONS = ['read', 'write', 'delete'];

interface CreateRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CreateRoleDialog({ open, onOpenChange }: CreateRoleDialogProps) {
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState<Record<string, Record<string, boolean>>>({});

  const NAME_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
  const nameError = name && !NAME_PATTERN.test(name)
    ? 'Lowercase letters, numbers, and hyphens only (e.g. custom-role)'
    : '';

  const createMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('POST', `/api/workspaces/${currentWorkspace?._id}/roles`, { name, displayName, description, permissions });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-roles'] });
      toast({ title: 'Role created successfully' });
      onOpenChange(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const resetForm = () => {
    setName('');
    setDisplayName('');
    setDescription('');
    setPermissions({});
  };

  const togglePermission = (resource: string, action: string) => {
    setPermissions((prev) => ({
      ...prev,
      [resource]: {
        ...prev[resource],
        [action]: !prev[resource]?.[action],
      },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Custom Role</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>Role Name (lowercase, no spaces)</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s/g, '-'))}
              placeholder="custom-role"
              className={nameError ? 'border-destructive' : ''}
            />
            {nameError && <p className="text-xs text-destructive mt-1">{nameError}</p>}
          </div>
          
          <div>
            <Label>Display Name</Label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Custom Role" />
          </div>
          
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          
          <div>
            <Label>Permissions</Label>
            <div className="border rounded-lg p-4 space-y-3 mt-2">
              {RESOURCES.map((resource) => (
                <div key={resource} className="flex items-center gap-4">
                  <span className="w-24 font-medium capitalize">{resource}</span>
                  {ACTIONS.map((action) => (
                    <label key={action} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={permissions[resource]?.[action] || false}
                        onCheckedChange={() => togglePermission(resource, action)}
                      />
                      <span className="text-sm">{action}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { resetForm(); onOpenChange(false); }}>Cancel</Button>
            <Button onClick={() => createMutation.mutate()} disabled={!name || !displayName || !!nameError || createMutation.isPending}>
              Create Role
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
